/* =========================================================================
   Deckenrechner – Frontend zur Testdatenbank (CSV-Lookup + Visualisierung)
   Institut für Baustatik und Konstruktion, ETH Zürich

   Drei Detaillierungsebenen:
   1 Vergleich – Deckensysteme über die Spannweite, Punkt wählen
   2 Analyse   – gewählte Varianten nebeneinander (GWP min / mittel / max)
   3 Detail    – eine Variante mit allen Kennwerten der Datenbank
   ========================================================================= */
"use strict";

/* ---------------------------------------------------------------------
   1. Spaltennamen der Datenbank
   --------------------------------------------------------------------- */
var COL = {
  crit: "criteria",
  source: "Datenquelle",
  use: "Nutzung",
  sound: "sound",
  sys: "Statisches System",
  sect: "section_type",
  floor: "Bodenaufbau",
  span: "l_tot [m]",
  conClass: "mech_prop",
  conId: "prod_id",
  conGwp: "GWP concrete [kgCO2eq / t]",
  refClass: "mech_prop_1",
  refId: "prod_id_1",
  refGwp: "GWP rebar [kgCO2eq / t]",
  hQs: "h_QS [m]",
  hTot: "h_tot [m]",
  hFloor: "h_Bodenaufbau [m]",
  b: "b [m]",
  bw: "b_w [m]",
  hf: "h_f [m]",
  rho: "Bew_Gehalt [kg/m3]",
  medP: "MEd_p [kNm]",
  medN: "MEd_n [kNm]",
  ved: "VEd [kN]",
  co2Rebar: "co2_rebar [kgCO2eq/m2]",
  co2Con: "co2_concrete [kgCO2eq/m2]",
  co2Struct: "co2 Struktur [kgCO2eq/m2]",
  co2Floor: "co2 Bodenaufbau [kgCO2eq/m2]",
  co2FloorA: "co2 Bodenaufbau pro Jahr [kgCO2eq/m2a]",
  co2Tot: "co2 [kgco2eq/m2]",
  co2TotA: "co2 pro Jahr [kgco2eq/m2a]",
  loadStruct: "Last Struktur [kN/m2]",
  loadFloor: "Last Bodenaufbau [kN/m2]",
  loadTot: "Last_tot [kN/m2]",
  lifespan: "lifespan Estrich [a]",
  /* Von der App abgeleitet (nicht in der CSV-Datei) */
  conGwpT: "_conGwpT",
  refGwpT: "_refGwpT",
  massStruct: "_massStruct",
  massFloor: "_massFloor",
  massTot: "_massTot"
};

/* GWP-Faktoren liegen in der Datenbank pro kg vor (Kopfzeile "/ t"). */
var GWP_PER_T = 1000;
/* Masse aus Eigenlast: m = g_k / g */
var GRAVITY = 9.81;

/* ---------------------------------------------------------------------
   2. Beschriftungen
   --------------------------------------------------------------------- */
var LOCALE = "de-CH";

var T = {
  /* Deckensysteme in Anzeigereihenfolge; ohne Datensätze erscheinen sie deaktiviert.
     Die Kürzel für Holz und HBV sind Annahmen, bis die Datenbank sie enthält. */
  decks: [
    { sect: "rc_rec", name: "Betonflachdecke" },
    { sect: "rc_rib", name: "Betonrippendecke" },
    { sect: "hbv_rib", name: "HBV-Rippendecke" },
    { sect: "wd_rec", name: "Massivholzdecke" }
  ],
  /* Statische Systeme in Anzeigereihenfolge: Bezeichnung, Erläuterung und Angaben
     für die Skizze (axes: Tragrichtungen, support: simple | fixed | point).
     Das Kürzel für "punktgelagert eingespannt" ist eine Annahme, bis die
     Datenbank das System enthält. */
  systems: {
    "BeamSimpleSup": { name: "einachsig tragend, einfach gelagert", note: "frei drehbar gelagert", axes: 1, support: "simple" },
    "BeamContinuousSupEl": { name: "einachsig tragend, durchlaufend", note: "beidseitig eingespannt", axes: 1, support: "fixed" },
    "Slab_LL-frei": { name: "zweiachsig tragend, einfach gelagert", note: "frei drehbar gelagert", axes: 2, support: "simple" },
    "Slab_LL-eingespannt": { name: "zweiachsig tragend, durchlaufend", note: "allseitig eingespannt", axes: 2, support: "fixed" },
    "Slab_punktgelagert": { name: "zweiachsig tragend, punktgelagert eingespannt", axes: 2, support: "point" }
  },
  critNames: {
    ENV: "ENV (Treibhauspotenzial)",
    GZT: "GZT (Tragsicherheit)",
    GZG: "GZG (Gebrauchstauglichkeit)",
    Fire: "Brandschutz"
  },
  scenNames: { min: "GWP min", mean: "GWP mittel", max: "GWP max" },
  floorNames: { massiv: "Zementestrich", Schuettung: "Zementestrich mit Schüttung" },
  soundNames: { min: "Mindestanforderungen" },
  noDataYet: "noch keine Daten",
  missing: "nicht in der Datenbank",
  noData: "Für diese Kombination liegt kein Datensatz vor.",
  loadError: "Die Datenbank konnte nicht geladen werden."
};

/* Weitere Eingabeparameter (grün markierte Spalten der Datenbank).
   Derzeit hat jeder nur einen Wert; mit weiteren Werten wird er wählbar. */
var EXTRAS = [
  { key: "use", col: COL.use, sel: "selUse", name: "Nutzung" },
  { key: "sound", col: COL.sound, sel: "selSound", name: "Schallschutz", names: T.soundNames },
  { key: "lifespan", col: COL.lifespan, sel: "selLifespan", name: "Nutzungsdauer Estrich", unit: "Jahre" },
  { key: "source", col: COL.source, sel: "selSource", name: "Datenquelle" }
];

/* ---------------------------------------------------------------------
   3. Darstellbare Grössen
   --------------------------------------------------------------------- */
/* Ebene 1: Umschalter über dem Diagramm; ohne Spalte deaktiviert */
var OVERVIEW = [
  { key: "gwp", label: "GWP", col: COL.co2Tot, unit: "kg CO₂eq/m²", dec: 1, name: "Treibhauspotenzial total" },
  { key: "height", label: "Höhe", col: COL.hTot, unit: "m", dec: 3, name: "Gesamthöhe inkl. Bodenaufbau" },
  { key: "mass", label: "Masse", col: COL.massTot, unit: "kg/m²", dec: 0, name: "Masse inkl. Bodenaufbau" },
  { key: "cost", label: "Kosten", col: null },
  { key: "ubp", label: "Graue Energie (UBP)", col: null }
];

/* Ebene 3: Auswahl im Diagramm */
var METRICS = [
  { col: COL.co2Tot, unit: "kg CO₂eq/m²", dec: 1, name: "Treibhauspotenzial total" },
  { col: COL.co2TotA, unit: "kg CO₂eq/m²a", dec: 3, name: "Treibhauspotenzial pro Jahr" },
  { col: COL.co2Struct, unit: "kg CO₂eq/m²", dec: 1, name: "Treibhauspotenzial Struktur" },
  { col: COL.co2Con, unit: "kg CO₂eq/m²", dec: 1, name: "Treibhauspotenzial Beton" },
  { col: COL.co2Rebar, unit: "kg CO₂eq/m²", dec: 1, name: "Treibhauspotenzial Betonstahl" },
  { col: COL.hTot, unit: "m", dec: 3, name: "Gesamthöhe h_tot" },
  { col: COL.hQs, unit: "m", dec: 3, name: "Querschnittshöhe h" },
  { col: COL.rho, unit: "kg/m³", dec: 1, name: "Bewehrungsgehalt" },
  { col: COL.massTot, unit: "kg/m²", dec: 0, name: "Masse total" },
  { col: COL.loadTot, unit: "kN/m²", dec: 2, name: "Ständige Last total" },
  { col: COL.medP, unit: "kNm", dec: 1, name: "Biegemoment M_Ed (Feld)" },
  { col: COL.ved, unit: "kN", dec: 1, name: "Querkraft V_Ed" }
];

/* Zeilendefinitionen: rib = nur Rippenquerschnitt, text = Text statt Zahl,
   missing = gewünscht, aber nicht in der Datenbank, mean = Mittelwert sinnvoll */
var R = {
  h: { col: COL.hQs, unit: "m", dec: 3, name: "Querschnittshöhe <i>h</i>" },
  hf: { col: COL.hf, unit: "m", dec: 3, name: "Plattendicke <i>h</i><sub>f</sub>", rib: true },
  bw: { col: COL.bw, unit: "m", dec: 3, name: "Stegbreite <i>b</i><sub>w</sub>", rib: true },
  bRec: { col: COL.b, unit: "m", dec: 3, name: "Breite <i>b</i>", rec: true },
  bRib: { col: COL.b, unit: "m", dec: 3, name: "Rippenabstand <i>b</i>", rib: true },
  hFloor: { col: COL.hFloor, unit: "m", dec: 3, name: "Höhe nichttragender Bodenaufbau" },
  hTot: { col: COL.hTot, unit: "m", dec: 3, name: "Gesamthöhe <i>h</i><sub>tot</sub>" },
  rho: { col: COL.rho, unit: "kg/m³", dec: 1, name: "Bewehrungsgehalt" },
  con: { col: COL.conClass, text: true, name: "Beton" },
  conId: { col: COL.conId, text: true, name: "Produkt-ID Beton" },
  conGwp: { col: COL.conGwpT, unit: "kg CO₂eq/t", dec: 1, name: "GWP-Faktor Beton" },
  ref: { col: COL.refClass, text: true, name: "Betonstahl" },
  refId: { col: COL.refId, text: true, name: "Produkt-ID Betonstahl" },
  refGwp: { col: COL.refGwpT, unit: "kg CO₂eq/t", dec: 1, name: "GWP-Faktor Betonstahl" },
  medP: { col: COL.medP, unit: "kNm", dec: 2, name: "<i>M</i><sub>Ed</sub> (Feld)" },
  medN: { col: COL.medN, unit: "kNm", dec: 2, name: "<i>M</i><sub>Ed</sub> (Stütze)" },
  ved: { col: COL.ved, unit: "kN", dec: 2, name: "<i>V</i><sub>Ed</sub>" },
  mrd: { missing: true, unit: "kNm", name: "<i>M</i><sub>Rd</sub>" },
  vrd: { missing: true, unit: "kN", name: "<i>V</i><sub>Rd</sub>" },
  loadStruct: { col: COL.loadStruct, unit: "kN/m²", dec: 3, name: "Last Struktur" },
  loadFloor: { col: COL.loadFloor, unit: "kN/m²", dec: 3, name: "Last nichttragender Bodenaufbau" },
  loadTot: { col: COL.loadTot, unit: "kN/m²", dec: 3, name: "Last total", strong: true },
  massStruct: { col: COL.massStruct, unit: "kg/m²", dec: 0, name: "Masse Struktur" },
  massFloor: { col: COL.massFloor, unit: "kg/m²", dec: 0, name: "Masse nichttragender Bodenaufbau" },
  massTot: { col: COL.massTot, unit: "kg/m²", dec: 0, name: "Masse total", strong: true },
  co2Rebar: { col: COL.co2Rebar, unit: "kg CO₂eq/m²", dec: 1, name: "GWP Betonstahl", mean: true },
  co2Con: { col: COL.co2Con, unit: "kg CO₂eq/m²", dec: 1, name: "GWP Beton", mean: true },
  co2Struct: { col: COL.co2Struct, unit: "kg CO₂eq/m²", dec: 1, name: "GWP Struktur", mean: true },
  co2Floor: { col: COL.co2Floor, unit: "kg CO₂eq/m²", dec: 1, name: "GWP nichttragender Bodenaufbau", mean: true },
  co2Tot: { col: COL.co2Tot, unit: "kg CO₂eq/m²", dec: 1, name: "GWP total", strong: true, mean: true },
  co2FloorA: { col: COL.co2FloorA, unit: "kg CO₂eq/m²a", dec: 3, name: "GWP nichttragender Bodenaufbau pro Jahr", mean: true },
  co2TotA: { col: COL.co2TotA, unit: "kg CO₂eq/m²a", dec: 3, name: "GWP total pro Jahr", mean: true },
  cost: { missing: true, unit: "CHF/m²", name: "Kosten" }
};

/* Ebene 1: Werte zum gewählten Punkt */
var POINT_ROWS = [
  { title: "Querschnitt", rows: [R.h, R.hf, R.bw, R.bRec, R.bRib, R.rho] },
  { title: "Kennwerte", rows: [R.co2Tot, R.hTot, R.massTot, R.cost] },
  { title: "Schnittgrössen (Bemessungswerte)", rows: [R.medP, R.medN, R.ved] }
];

/* Ebene 2: Karte je Variante */
var CARD_ROWS = [
  { rows: [R.co2Tot, R.hTot, R.h, R.hf, R.bw, R.bRec, R.bRib, R.rho, R.massTot, R.cost] }
];

/* Ebene 3: alle Kennwerte */
var DETAIL_ROWS = [
  { title: "Geometrie", rows: [R.h, R.hf, R.bw, R.bRec, R.bRib, R.hFloor, R.hTot] },
  { title: "Material und Bewehrung", rows: [R.con, R.conId, R.conGwp, R.ref, R.refId, R.refGwp, R.rho] },
  { title: "Schnittgrössen und Widerstände", rows: [R.medP, R.medN, R.ved, R.mrd, R.vrd] },
  { title: "Lasten und Masse", rows: [R.loadStruct, R.loadFloor, R.loadTot, R.massStruct, R.massFloor, R.massTot] },
  { title: "Treibhauspotenzial (GWP)", rows: [R.co2Rebar, R.co2Con, R.co2Struct, R.co2Floor, R.co2Tot, R.co2FloorA, R.co2TotA] },
  { title: "Kosten", rows: [R.cost] }
];

/* Anteile im Säulendiagramm der Ebene 2 (von unten nach oben) */
var STACK = [
  { col: COL.co2Rebar, name: "Betonstahl", color: "#2a78d6" },
  { col: COL.co2Con, name: "Beton", color: "#1baf7a" },
  { col: COL.co2Floor, name: "Nichttragender Bodenaufbau", color: "#eda100" }
];

/* Schichten des nichttragenden Bodenaufbaus von oben nach unten, Dicke t in m
   (noch nicht in der Datenbank; im Querschnitt auf h_Bodenaufbau skaliert) */
var FLOOR_LAYERS = {
  massiv: [
    { name: "Parkett", t: 0.011, pat: "parkett" },
    { name: "Zementestrich", t: 0.085, pat: "estrich" },
    { name: "Glaswolle", t: 0.030, pat: "wolle" }
  ],
  Schuettung: [
    { name: "Parkett", t: 0.011, pat: "parkett" },
    { name: "Zementestrich", t: 0.085, pat: "estrich" },
    { name: "Glaswolle", t: 0.030, pat: "wolle" },
    { name: "Kiesschüttung", t: 0.060, pat: "kies" }
  ]
};

/* ---------------------------------------------------------------------
   4. Farben (validierte kategoriale Palette, Hellmodus)
   --------------------------------------------------------------------- */
var SERIES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];
/* Statische Systeme eines Deckensystems: gleiche Farbe, eigene Linienart und Markierung */
var DASHES = [
  { plotly: "solid", svg: "" },
  { plotly: "dash", svg: "6 4" },
  { plotly: "dot", svg: "2 3" },
  { plotly: "dashdot", svg: "6 3 2 3" }
];
var SYMBOLS = ["circle", "square", "diamond", "triangle-up"];
var INK = { primary: "#0b0b0b", secondary: "#52514e", muted: "#8b8a85", grid: "#e6e5e1", surface: "#ffffff" };
var CRIT_ORDER = ["ENV", "GZT", "GZG", "Fire"];

/* ---------------------------------------------------------------------
   5. Hilfsfunktionen
   --------------------------------------------------------------------- */
function num(v) {
  if (v === undefined || v === null || v === "") return NaN;
  return parseFloat(v);
}

function fmt(v, dec) {
  if (v === undefined || v === null || v === "" || isNaN(num(v))) return "–";
  return num(v).toLocaleString(LOCALE, {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec
  });
}

function clean(v) {
  return String(v).replace(/^'|'$/g, "");
}

function el(id) {
  return document.getElementById(id);
}

function rgba(hex, a) {
  var n = parseInt(hex.slice(1), 16);
  return "rgba(" + (n >> 16) + "," + ((n >> 8) & 255) + "," + (n & 255) + "," + a + ")";
}

function copy(o) {
  var out = {};
  Object.keys(o).forEach(function (k) { out[k] = o[k]; });
  return out;
}

/* Einfacher CSV-Parser – die Datenbank enthält keine maskierten Trennzeichen. */
function parseCSV(text) {
  var lines = text.replace(/^﻿/, "").split(/\r?\n/);
  var head = lines[0].split(",");
  var out = [];
  for (var i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    var cells = lines[i].split(",");
    var row = {};
    for (var j = 0; j < head.length; j++) row[head[j]] = (cells[j] || "").trim();
    /* Zeilennummer in der Datei (= Zeile in der Excel-Vorlage), nicht aufzählbar */
    Object.defineProperty(row, "line", { value: i + 1 });
    out.push(row);
  }
  return { head: head, rows: out };
}

/* ---------------------------------------------------------------------
   6. Datenbank-Index

   Jede Festigkeitsklasse (Beton) bzw. Stahlsorte kommt mit zwei Produkten
   vor: dem mit dem kleinsten und dem mit dem grössten GWP-Faktor. Daraus
   entstehen die Szenarien "min" (beide Faktoren minimal) und "max" (beide
   maximal). Gemischte Kombinationen werden nicht angezeigt.
   --------------------------------------------------------------------- */
var DB = {
  head: [],
  rows: [],
  index: {},          // Lookup-Schlüssel -> Zeile
  decks: [],          // [{sect, name, systems: [sysKey], available}]
  systems: {},        // sysKey -> {key, sect, sys, name}
  spans: {},          // sysKey -> [Spannweiten]
  allSpans: [],
  classes: [],        // Betonfestigkeitsklassen [{key, name, min, max}]
  rebarClasses: [],
  floors: [],
  criteria: [],
  extras: {}          // EXTRAS-Schlüssel -> [{key, name}]
};

function rowKey(p, scen) {
  return [p.crit, p.sysKey, p.floor, p.cls, p.refCls, scen, p.span,
    EXTRAS.map(function (e) { return p[e.key]; }).join("|")].join("|");
}

/* Produkt-ID -> Szenarien, je Klasse kleinster und grösster GWP-Faktor */
function gwpRoles(rows, clsCol, idCol, gwpCol) {
  var byCls = {};
  rows.forEach(function (r) {
    var c = r[clsCol];
    (byCls[c] = byCls[c] || {})[r[idCol]] = num(r[gwpCol]);
  });
  var roles = {}, classes = [];
  Object.keys(byCls).forEach(function (c) {
    var gwp = byCls[c];
    var ids = Object.keys(gwp).sort(function (a, b) { return gwp[a] - gwp[b]; });
    var lo = ids[0], hi = ids[ids.length - 1];
    if (ids.length > 2) {
      console.warn("Deckenrechner: " + ids.length + " Produkte für " + clean(c) + ", verwendet werden nur Minimum und Maximum.");
    }
    roles[lo] = lo === hi ? ["min", "max"] : ["min"];
    if (lo !== hi) roles[hi] = ["max"];
    classes.push({
      key: c,
      min: gwp[lo] * GWP_PER_T,
      max: gwp[hi] * GWP_PER_T,
      name: clean(c) + " · " + fmt(gwp[lo] * GWP_PER_T, 1) + "–" + fmt(gwp[hi] * GWP_PER_T, 1) + " kg CO₂eq/t"
    });
  });
  classes.sort(function (a, b) { return a.key.localeCompare(b.key); });
  return { roles: roles, classes: classes };
}

function buildIndex(csv) {
  var rows = csv.rows;
  DB.head = csv.head;
  DB.rows = rows;

  var con = gwpRoles(rows, COL.conClass, COL.conId, COL.conGwp);
  var ref = gwpRoles(rows, COL.refClass, COL.refId, COL.refGwp);
  DB.classes = con.classes;
  DB.rebarClasses = ref.classes;

  var seen = { floor: {}, crit: {}, span: {} };
  EXTRAS.forEach(function (e) { DB.extras[e.key] = []; seen[e.key] = {}; });

  rows.forEach(function (r) {
    r[COL.conGwpT] = num(r[COL.conGwp]) * GWP_PER_T;
    r[COL.refGwpT] = num(r[COL.refGwp]) * GWP_PER_T;
    r[COL.massStruct] = num(r[COL.loadStruct]) * 1000 / GRAVITY;
    r[COL.massFloor] = num(r[COL.loadFloor]) * 1000 / GRAVITY;
    r[COL.massTot] = num(r[COL.loadTot]) * 1000 / GRAVITY;

    var sysKey = r[COL.sect] + "|" + r[COL.sys];
    var span = num(r[COL.span]);
    var p = { crit: r[COL.crit], sysKey: sysKey, floor: r[COL.floor], cls: r[COL.conClass], refCls: r[COL.refClass], span: span };
    EXTRAS.forEach(function (e) { p[e.key] = r[e.col]; });

    var conRoles = con.roles[r[COL.conId]] || [];
    (ref.roles[r[COL.refId]] || []).forEach(function (scen) {
      if (conRoles.indexOf(scen) === -1) return;
      var key = rowKey(p, scen);
      if (DB.index[key]) console.warn("Deckenrechner: CSV-Zeilen " + DB.index[key].line + " und " + r.line + " haben denselben Schlüssel.");
      DB.index[key] = r;
    });

    if (!DB.systems[sysKey]) {
      DB.systems[sysKey] = {
        key: sysKey,
        sect: r[COL.sect],
        sys: r[COL.sys],
        name: T.systems[r[COL.sys]] ? T.systems[r[COL.sys]].name : r[COL.sys]
      };
      DB.spans[sysKey] = [];
    }
    if (DB.spans[sysKey].indexOf(span) === -1) DB.spans[sysKey].push(span);
    if (!seen.span[span]) { seen.span[span] = true; DB.allSpans.push(span); }
    if (!seen.floor[r[COL.floor]]) {
      seen.floor[r[COL.floor]] = true;
      DB.floors.push({ key: r[COL.floor], name: T.floorNames[r[COL.floor]] || r[COL.floor] });
    }
    if (!seen.crit[r[COL.crit]]) { seen.crit[r[COL.crit]] = true; DB.criteria.push(r[COL.crit]); }
    EXTRAS.forEach(function (e) {
      var v = r[e.col];
      if (seen[e.key][v]) return;
      seen[e.key][v] = true;
      var name = (e.names && e.names[v]) || v;
      DB.extras[e.key].push({ key: v, name: e.unit ? name + " " + e.unit : name });
    });
  });

  /* Deckensysteme: bekannte in fester Reihenfolge, unbekannte hinten angehängt */
  var sysOrder = Object.keys(T.systems);
  var sysKeys = Object.keys(DB.systems).sort(function (a, b) {
    return sysOrder.indexOf(DB.systems[a].sys) - sysOrder.indexOf(DB.systems[b].sys);
  });
  DB.decks = T.decks.map(function (d) { return { sect: d.sect, name: d.name }; });
  sysKeys.forEach(function (k) {
    var sect = DB.systems[k].sect;
    if (!DB.decks.some(function (d) { return d.sect === sect; })) DB.decks.push({ sect: sect, name: sect });
  });
  DB.decks.forEach(function (d, i) {
    d.color = SERIES[i % SERIES.length];
    d.systems = sysKeys.filter(function (k) { return DB.systems[k].sect === d.sect; });
    d.available = d.systems.length > 0;
  });

  /* Stabile, nachvollziehbare Reihenfolgen */
  DB.floors.sort(function (a, b) { return a.key.localeCompare(b.key); });
  DB.criteria.sort(function (a, b) { return critIndex(a) - critIndex(b); });
  DB.allSpans.sort(function (a, b) { return a - b; });
  Object.keys(DB.spans).forEach(function (k) { DB.spans[k].sort(function (a, b) { return a - b; }); });
  EXTRAS.forEach(function (e) {
    DB.extras[e.key].sort(function (a, b) { return num(a.key) - num(b.key) || a.key.localeCompare(b.key); });
  });
}

function critIndex(c) {
  var i = CRIT_ORDER.indexOf(c);
  return i === -1 ? CRIT_ORDER.length : i;
}

function deckOf(sysKey) {
  var sect = DB.systems[sysKey].sect;
  return DB.decks.filter(function (d) { return d.sect === sect; })[0];
}

/* ---------------------------------------------------------------------
   7. Zustand der Auswahl

   Eine Variante (p) ist ein Satz Parameter: System, Spannweite, Material,
   Bodenaufbau, Kriterium und die EXTRAS. Ebene 2 speichert Kopien davon.
   --------------------------------------------------------------------- */
var state = {
  systems: [],      // angezeigte statische Systeme (sysKey), auch mehrere je Deckensystem
  focus: null,      // sysKey des gewählten Punkts in Ebene 1
  span: null,
  cls: null,
  refCls: null,
  floor: null,
  crit: null,
  spread: true,     // Streuung (GWP min–max) anzeigen
  metric: "gwp",    // Ebene 1
  scen: "min",      // dargestellter Querschnitt
  detailMetric: 0,  // Ebene 3
  cmpScen: "all",   // Ebene 2: alle Szenarien je Variante oder ein Szenario für alle Varianten
  compare: [],      // Varianten der Ebene 2
  detail: null,     // Variante der Ebene 3; null = Auswahl der Ebene 1
  level: 1
};

function current(over) {
  var p = {
    crit: state.crit, sysKey: state.focus, floor: state.floor,
    cls: state.cls, refCls: state.refCls, span: state.span
  };
  EXTRAS.forEach(function (e) { p[e.key] = state[e.key]; });
  if (over) Object.keys(over).forEach(function (k) { p[k] = over[k]; });
  return p;
}

function withParams(p, over) {
  var q = copy(p);
  Object.keys(over).forEach(function (k) { q[k] = over[k]; });
  return q;
}

function find(p, scen) {
  return DB.index[rowKey(p, scen)];
}

function pair(p) {
  return { min: find(p, "min"), max: find(p, "max") };
}

/* Mittelwert aus min und max – bis die Datenbank eigene Mittelwert-Datensätze enthält */
function mid(pr, col) {
  if (!pr.min || !pr.max) return NaN;
  return (num(pr.min[col]) + num(pr.max[col])) / 2;
}

/* style: Index des Systems innerhalb seines Deckensystems (Linienart) */
function activeSystems() {
  var out = [];
  DB.decks.forEach(function (d) {
    d.systems.forEach(function (k, i) {
      if (state.systems.indexOf(k) !== -1) out.push({ key: k, deck: d, style: i });
    });
  });
  return out;
}

function sameVariant(a, b) {
  return rowKey(a, "") === rowKey(b, "");
}

function describe(p) {
  var parts = [DB.systems[p.sysKey].name, "l = " + fmt(p.span, 1) + " m", clean(p.cls)];
  var floor = DB.floors.filter(function (f) { return f.key === p.floor; })[0];
  parts.push("Nichttragender Bodenaufbau " + (floor ? floor.name : p.floor));
  if (DB.rebarClasses.length > 1) parts.push(clean(p.refCls));
  if (DB.criteria.length > 1) parts.push(T.critNames[p.crit] || p.crit);
  EXTRAS.forEach(function (e) {
    if (DB.extras[e.key].length < 2) return;
    var o = DB.extras[e.key].filter(function (x) { return x.key === p[e.key]; })[0];
    parts.push(o ? o.name : p[e.key]);
  });
  return parts.join(" · ");
}

/* ---------------------------------------------------------------------
   8. Bedienelemente
   --------------------------------------------------------------------- */
function fillSelect(id, items, selected) {
  var sel = el(id);
  sel.innerHTML = "";
  items.forEach(function (it) {
    var o = document.createElement("option");
    o.value = it.key;
    o.textContent = it.name;
    if (it.key === selected) o.selected = true;
    sel.appendChild(o);
  });
}

var spanSlider = null;

function buildSpanSlider() {
  var node = el("sliderSpan");
  var values = DB.allSpans;

  noUiSlider.create(node, {
    start: [values.indexOf(state.span)],
    step: 1,
    connect: [true, false],
    range: { min: 0, max: values.length - 1 },
    pips: {
      mode: "steps",
      density: 100 / Math.max(values.length - 1, 1),
      format: { to: function (v) { return values[Math.round(v)]; }, from: function (v) { return v; } }
    },
    format: { to: function (v) { return Math.round(v); }, from: function (v) { return Number(v); } }
  });
  spanSlider = node.noUiSlider;
  spanSlider.on("update", function (vals) {
    state.span = values[Number(vals[0])];
    el("spanValue").textContent = fmt(state.span, 1) + " m";
  });
  spanSlider.on("change", render);
}

function setSpan(span) {
  spanSlider.set(DB.allSpans.indexOf(span));
}

/* Linienmuster wie im Diagramm, vor dem Namen des statischen Systems */
function lineSwatch(color, style) {
  return '<svg class="sysSwatch" width="22" height="10" aria-hidden="true"><line x1="1" y1="5" x2="21" y2="5" stroke="' +
    color + '" stroke-width="2" stroke-dasharray="' + DASHES[style % DASHES.length].svg + '"/></svg>';
}

/* Je Deckensystem ein Kontrollkästchen (alle / keine) und darunter eines je statischem System */
function buildDeckList() {
  var html = "";
  DB.decks.forEach(function (d) {
    var id = "deck_" + d.sect;
    html += '<div class="deckItem' + (d.available ? "" : " deckDisabled") + '">' +
      '<div class="custom-control custom-checkbox">' +
      '<input type="checkbox" class="custom-control-input" id="' + id + '" data-deck="' + d.sect + '"' +
      (d.available ? "" : " disabled") + ">" +
      '<label class="custom-control-label" for="' + id + '">' +
      '<span class="deckSwatch" style="background:' + d.color + '"></span>' + d.name +
      (d.available ? "" : ' <span class="deckNote">' + T.noDataYet + "</span>") + "</label></div>";
    d.systems.forEach(function (k, i) {
      var sid = id + "_" + i;
      var s = T.systems[DB.systems[k].sys];
      html += '<div class="custom-control custom-checkbox deckSystem">' +
        '<input type="checkbox" class="custom-control-input" id="' + sid + '" data-sys="' + k + '"' +
        (state.systems.indexOf(k) !== -1 ? " checked" : "") + ">" +
        '<label class="custom-control-label" for="' + sid + '"' + (s ? ' title="' + systemLabel(s) + '"' : "") + ">" +
        lineSwatch(d.color, i) + DB.systems[k].name + "</label></div>";
    });
    html += "</div>";
  });
  el("deckList").innerHTML = html;
  syncDeckList();
}

function syncDeckList() {
  Array.prototype.forEach.call(document.querySelectorAll("#deckList input[data-sys]"), function (box) {
    box.checked = state.systems.indexOf(box.getAttribute("data-sys")) !== -1;
  });
  DB.decks.forEach(function (d) {
    var n = d.systems.filter(function (k) { return state.systems.indexOf(k) !== -1; }).length;
    var box = el("deck_" + d.sect);
    box.checked = n > 0 && n === d.systems.length;
    box.indeterminate = n > 0 && n < d.systems.length;
  });
}

function onDeckChange(ev) {
  var box = ev.target;
  var sect = box.getAttribute("data-deck");
  var keys = sect ? DB.decks.filter(function (d) { return d.sect === sect; })[0].systems : [box.getAttribute("data-sys")];
  keys.forEach(function (k) {
    var i = state.systems.indexOf(k);
    if (box.checked && i === -1) state.systems.push(k);
    if (!box.checked && i !== -1) state.systems.splice(i, 1);
  });
  syncDeckList();
  /* Gewählter Punkt bleibt, solange sein System angezeigt wird; sonst ein anderes
     System desselben Deckensystems oder das erste angezeigte */
  var active = activeSystems();
  if (state.focus && state.systems.indexOf(state.focus) === -1) {
    var focusDeck = DB.systems[state.focus].sect;
    var next = active.filter(function (s) { return s.deck.sect === focusDeck; })[0] || active[0];
    if (next) state.focus = next.key;
  }
  render();
}

function setButtons(group, value) {
  Array.prototype.forEach.call(document.querySelectorAll("[data-" + group + "]"), function (b) {
    b.classList.toggle("active", b.getAttribute("data-" + group) === value);
  });
}

function buildControls() {
  fillSelect("selClass", DB.classes, state.cls);
  fillSelect("selRebarClass", DB.rebarClasses, state.refCls);
  fillSelect("selFloor", DB.floors, state.floor);
  EXTRAS.forEach(function (e) { fillSelect(e.sel, DB.extras[e.key], state[e.key]); });
  buildDeckList();
  buildSpanSlider();

  /* Umschalter Ebene 1 */
  el("metricButtons").innerHTML = OVERVIEW.map(function (m) {
    return '<button type="button" class="btn btn-outline-secondary' + (m.key === state.metric ? " active" : "") + '"' +
      ' data-metric="' + m.key + '"' + (m.col ? "" : ' disabled title="' + T.noDataYet + '"') + ">" + m.label + "</button>";
  }).join("");
  el("metricButtons").addEventListener("click", function (ev) {
    var key = ev.target.getAttribute("data-metric");
    if (!key) return;
    state.metric = key;
    setButtons("metric", key);
    render();
  });

  /* Szenario des Querschnitts (Ebene 1 und 3) */
  Array.prototype.forEach.call(document.querySelectorAll(".scenToggle"), function (g) {
    g.addEventListener("click", function (ev) {
      var scen = ev.target.getAttribute("data-scen");
      if (!scen) return;
      state.scen = scen;
      setButtons("scen", scen);
      render();
    });
  });

  /* Szenario der Analyse (Ebene 2) */
  el("cmpScenButtons").addEventListener("click", function (ev) {
    var scen = ev.target.getAttribute("data-cmpscen");
    if (!scen) return;
    state.cmpScen = scen;
    setButtons("cmpscen", scen);
    render();
  });

  fillSelect("selDetailMetric", METRICS.map(function (m, i) { return { key: String(i), name: m.name }; }), "0");
  el("selDetailMetric").addEventListener("change", function () { state.detailMetric = Number(this.value); render(); });

  el("deckList").addEventListener("change", onDeckChange);
  el("chkSpread").checked = state.spread;
  el("chkSpread").addEventListener("change", function () { state.spread = this.checked; render(); });
  el("selClass").addEventListener("change", function () { state.cls = this.value; render(); });
  el("selRebarClass").addEventListener("change", function () { state.refCls = this.value; render(); });
  el("selFloor").addEventListener("change", function () { state.floor = this.value; render(); });
  EXTRAS.forEach(function (e) {
    el(e.sel).addEventListener("change", function () { state[e.key] = this.value; render(); });
  });

  el("btnAddCompare").addEventListener("click", function () {
    var p = current();
    if (!state.compare.some(function (q) { return sameVariant(q, p); })) state.compare.push(p);
    render();
  });
  el("btnDetail").addEventListener("click", function () {
    state.detail = null;
    $("#tab3").tab("show");
  });
  el("btnDetailReset").addEventListener("click", function () {
    state.detail = null;
    render();
  });
  el("cmpCards").addEventListener("click", function (ev) {
    var btn = ev.target.closest("[data-action]");
    if (!btn) return;
    var i = Number(btn.getAttribute("data-index"));
    if (btn.getAttribute("data-action") === "remove") {
      state.compare.splice(i, 1);
      render();
    } else {
      state.detail = copy(state.compare[i]);
      $("#tab3").tab("show");
    }
  });

  $('a[data-toggle="tab"]').on("shown.bs.tab", function (ev) {
    state.level = Number(ev.target.getAttribute("data-level"));
    render();
  });
}

/* ---------------------------------------------------------------------
   9. Tabellen
   --------------------------------------------------------------------- */
function cell(row, def) {
  if (def.missing) return '<span class="valueMissing" title="' + T.missing + '">–</span>';
  if (!row) return "–";
  if (def.text) return clean(row[def.col]);
  return fmt(row[def.col], def.dec);
}

/* Ebene 2: Breite der Bezeichnungs- und Einheitenspalte in px. Das Diagramm darüber
   hat dieselben Ränder, damit jede Säule über ihrer Wertespalte steht. */
var ALIGN = { label: 150, unit: 96 };

/* groups: [{title, rows}], heads: Spaltenköpfe oder null, cols: [function(def) -> html],
   isRib: Zeilen für Rippen- bzw. Flachdecke; null zeigt beide (Spalten gemischt),
   corner: Text der Kopfzeile über den Bezeichnungen (optional),
   aligned: Spaltenbreiten nach ALIGN, Werte zentriert unter den Säulen */
function valueTable(groups, heads, cols, isRib, corner, aligned) {
  var span = cols.length + 2;
  var html = '<table class="table table-sm resultTable' + (aligned ? " alignedTable" : "") + '">';
  if (aligned) {
    html += '<colgroup><col style="width:' + ALIGN.label + 'px">' + cols.map(function () { return "<col>"; }).join("") +
      '<col style="width:' + ALIGN.unit + 'px"></colgroup>';
  }
  if (heads) {
    html += '<thead><tr><th class="resultHead">' + (corner || "") + "</th>" + heads.map(function (h) {
      return '<th class="text-right resultHead">' + h + "</th>";
    }).join("") + "<th></th></tr></thead>";
  }
  groups.forEach(function (g) {
    html += "<tbody>";
    if (g.title) html += '<tr><th colspan="' + span + '" class="resultGroup">' + g.title + "</th></tr>";
    g.rows.forEach(function (d) {
      if (isRib !== null && ((d.rib && !isRib) || (d.rec && isRib))) return;
      html += "<tr" + (d.strong ? ' class="resultStrong"' : "") + "><td>" + d.name + "</td>" +
        cols.map(function (c) { return '<td class="text-right resultValue">' + c(d) + "</td>"; }).join("") +
        '<td class="resultUnit">' + (d.unit || "") + "</td></tr>";
    });
    html += "</tbody>";
  });
  return html + "</table>";
}

function isRibRow(row) {
  return row && row[COL.sect] === "rc_rib";
}

/* ---------------------------------------------------------------------
   10. Querschnitt (SVG, massstäblich; Bewehrung schematisch)
   --------------------------------------------------------------------- */
function sym(base, sub) {
  return '<tspan font-style="italic">' + base + "</tspan>" +
    (sub ? '<tspan font-size="9" dy="3">' + sub + '</tspan><tspan dy="-3"></tspan>' : "");
}

function mm(v) {
  return fmt(v * 1000, 0);
}

function svgLine(x1, y1, x2, y2, cls) {
  return '<line class="' + cls + '" x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2 + '"/>';
}

/* Masslinie mit Schrägstrichen an den Enden */
function dimV(x, ya, yb, label, side) {
  var t = 4;
  return svgLine(x, ya, x, yb, "secDim") +
    svgLine(x - t, ya + t, x + t, ya - t, "secDim") + svgLine(x - t, yb + t, x + t, yb - t, "secDim") +
    '<text class="secDimText" x="' + (x + side * 7) + '" y="' + ((ya + yb) / 2 + 4) + '" text-anchor="' +
    (side < 0 ? "end" : "start") + '">' + label + "</text>";
}

function dimH(y, xa, xb, label) {
  var t = 4;
  return svgLine(xa, y, xb, y, "secDim") +
    svgLine(xa - t, y + t, xa + t, y - t, "secDim") + svgLine(xb - t, y + t, xb + t, y - t, "secDim") +
    '<text class="secDimText" x="' + ((xa + xb) / 2) + '" y="' + (y + 15) + '" text-anchor="middle">' + label + "</text>";
}

/* Bewehrungslage: Stäbe in x-Richtung (Punkte) und Lage in y-Richtung (Linie) */
function rebarLayer(xa, xb, y, r, s, lineSide) {
  var out = svgLine(xa, y + lineSide * (r + 1.5), xb, y + lineSide * (r + 1.5), "secRebarLine");
  var n = Math.max(3, Math.min(24, Math.round((xb - xa) / s / 0.15)));
  for (var i = 0; i < n; i++) {
    var x = xa + r + (xb - xa - 2 * r) * i / (n - 1);
    out += '<circle class="secRebar" cx="' + x + '" cy="' + y + '" r="' + r + '"/>';
  }
  return out;
}

/* Schraffuren der Bodenschichten; IDs je SVG eindeutig, da mehrere Querschnitte im DOM stehen */
var secCount = 0;

function floorPatterns(id) {
  var p = {
    parkett: '<line class="secHatch" x1="0" y1="6" x2="6" y2="0"/>',
    estrich: '<line class="secHatch" x1="0" y1="0" x2="8" y2="8"/><line class="secHatch" x1="0" y1="8" x2="8" y2="0"/>',
    wolle: '<line class="secHatch" x1="2" y1="0" x2="2" y2="4"/>',
    kies: [[2, 3], [7, 1.5], [10, 7], [4, 9], [8.5, 10.5], [1, 7]].map(function (c) {
      return '<circle class="secGrain" cx="' + c[0] + '" cy="' + c[1] + '" r="0.8"/>';
    }).join("")
  };
  var size = { parkett: 6, estrich: 8, wolle: 4, kies: 12 };
  return "<defs>" + Object.keys(p).map(function (k) {
    return '<pattern id="' + id + k + '" width="' + size[k] + '" height="' + size[k] + '" patternUnits="userSpaceOnUse">' +
      '<rect class="secPat-' + k + '" width="' + size[k] + '" height="' + size[k] + '"/>' + p[k] + "</pattern>";
  }).join("") + "</defs>";
}

function sectionSvg(row) {
  var rib = isRibRow(row);
  var h = num(row[COL.hQs]), hFl = num(row[COL.hFloor]) || 0;
  var b = num(row[COL.b]);
  if (isNaN(b)) b = 1;
  var hf = rib ? num(row[COL.hf]) : h, bw = rib ? num(row[COL.bw]) : b;
  var layers = hFl > 0 ? FLOOR_LAYERS[row[COL.floor]] : null;
  var id = "sec" + (++secCount) + "-";

  var W = 480, padX = 90, padT = 8, padB = rib ? 62 : 38, maxH = 220;
  var s = Math.min((W - 2 * padX) / b, maxH / (h + hFl));
  var x0 = (W - b * s) / 2, x1 = x0 + b * s;
  var yF = padT, yC = yF + hFl * s, yW = yC + hf * s, yB = yC + h * s;
  var xw0 = W / 2 - bw * s / 2, xw1 = W / 2 + bw * s / 2;
  var yL = yB + padB, H = yL + (layers ? Math.ceil(layers.length / 2) * 18 : 0);
  var o = [];

  if (layers) {
    /* Schichten massstäblich, Summe auf h_Bodenaufbau der Datenbank skaliert */
    var tSum = layers.reduce(function (a, l) { return a + l.t; }, 0), y = yF;
    o.push(floorPatterns(id));
    layers.forEach(function (l, i) {
      var t = l.t / tSum * (yC - yF);
      o.push('<rect class="secLayer" x="' + x0 + '" y="' + y + '" width="' + (x1 - x0) + '" height="' + t +
        '" fill="url(#' + id + l.pat + ')"/>');
      y += t;
      /* Legende zweispaltig unter dem Querschnitt */
      var lx = i % 2 ? W / 2 + 10 : padX, ly = yL + Math.floor(i / 2) * 18;
      o.push('<rect class="secLayer" x="' + lx + '" y="' + ly + '" width="24" height="10" fill="url(#' + id + l.pat + ')"/>' +
        '<text class="secLegend" x="' + (lx + 32) + '" y="' + (ly + 9) + '">' + l.name + " " + mm(l.t) + " mm</text>");
    });
  } else if (hFl > 0) {
    o.push('<rect class="secFloor" x="' + x0 + '" y="' + yF + '" width="' + (x1 - x0) + '" height="' + (yC - yF) + '"/>');
    if (yC - yF >= 14) {
      o.push('<text class="secNote" x="' + (W / 2) + '" y="' + ((yF + yC) / 2 + 4) + '" text-anchor="middle">Nichttragender Bodenaufbau</text>');
    }
  }
  if (rib) {
    o.push('<path class="secConcrete" d="M' + x0 + "," + yC + "H" + x1 + "V" + yW + "H" + xw1 + "V" + yB +
      "H" + xw0 + "V" + yW + "H" + x0 + 'Z"/>');
  } else {
    o.push('<rect class="secConcrete" x="' + x0 + '" y="' + yC + '" width="' + (x1 - x0) + '" height="' + (yB - yC) + '"/>');
  }

  /* Bewehrung schematisch: Überdeckung ca. 30 mm, Abstand ca. 150 mm */
  var r = 2.5;
  var c = Math.min(Math.max(r + 2, 0.03 * s), (yW - yC) / 4 + r);
  o.push(rebarLayer(x0 + c, x1 - c, yC + c, r, s, 1));
  o.push(rebarLayer(x0 + c, x1 - c, yW - c, r, s, -1));
  if (rib) {
    var cs = Math.min(c, (xw1 - xw0) / 5);
    o.push('<rect class="secStirrup" x="' + (xw0 + cs) + '" y="' + (yC + cs) + '" width="' + (xw1 - xw0 - 2 * cs) +
      '" height="' + (yB - yC - 2 * cs) + '" rx="3"/>');
    var rw = Math.min(4, (xw1 - xw0 - 2 * cs) / 7), nw = (xw1 - xw0) > 30 ? 3 : 2;
    for (var i = 0; i < nw; i++) {
      var x = xw0 + cs + rw + 1 + (xw1 - xw0 - 2 * cs - 2 * rw - 2) * i / (nw - 1);
      o.push('<circle class="secRebar" cx="' + x + '" cy="' + (yB - cs - rw - 1) + '" r="' + rw + '"/>');
    }
  }

  /* Masse in mm */
  o.push(dimV(x0 - 16, yC, yB, sym("h") + " = " + mm(h), -1));
  if (hFl > 0) o.push(dimV(x0 - 16, yF, yC, mm(hFl), -1));
  if (rib) {
    o.push(dimV(x1 + 16, yC, yW, sym("h", "f") + " = " + mm(hf), 1));
    o.push(dimH(yB + 14, xw0, xw1, sym("b", "w") + " = " + mm(bw)));
    o.push(dimH(yB + 38, x0, x1, sym("b") + " = " + mm(b)));
  } else {
    o.push(dimV(x1 + 16, yF, yB, sym("h", "tot") + " = " + mm(num(row[COL.hTot])), 1));
    o.push(dimH(yB + 14, x0, x1, sym("b") + " = " + mm(b)));
  }

  return '<svg class="secSvg" viewBox="0 0 ' + W + " " + Math.ceil(H) + '" role="img" aria-label="Querschnitt, h = ' +
    mm(h) + ' mm">' + o.join("") + "</svg>";
}

/* ---------------------------------------------------------------------
   10a. Statisches System (SVG, schematisch, nicht massstäblich)
   --------------------------------------------------------------------- */
function xy(x, y) {
  return { x: x, y: y };
}

function vAdd(p, v, f) {
  f = f === undefined ? 1 : f;
  return xy(p.x + v.x * f, p.y + v.y * f);
}

function vSub(p, q) {
  return xy(p.x - q.x, p.y - q.y);
}

function vLerp(p, q, f) {
  return vAdd(p, vSub(q, p), f);
}

function vUnit(v) {
  var n = Math.hypot(v.x, v.y);
  return xy(v.x / n, v.y / n);
}

function polyPoints(ps) {
  return ps.map(function (p) { return p.x.toFixed(1) + "," + p.y.toFixed(1); }).join(" ");
}

/* Masslinie von p nach q, Beschriftung um (lx, ly) neben der Mitte.
   Endstriche um 45° zur Masslinie gedreht (waagrecht wie bei dimH), damit
   sie auch bei schrägen Masslinien deutlich sichtbar sind. */
function dimAlong(p, q, label, lx, ly, anchor) {
  var u = vUnit(vSub(q, p)), m = vLerp(p, q, 0.5);
  var k = xy((u.x + u.y) * 4, (u.y - u.x) * 4);
  return svgLine(p.x, p.y, q.x, q.y, "secDim") +
    svgLine(p.x - k.x, p.y - k.y, p.x + k.x, p.y + k.y, "secDim") + svgLine(q.x - k.x, q.y - k.y, q.x + k.x, q.y + k.y, "secDim") +
    '<text class="secDimText" x="' + (m.x + lx) + '" y="' + (m.y + ly) + '" text-anchor="' + (anchor || "middle") + '">' + label + "</text>";
}

/* Spannweite als Beschriftung der Masslinie */
function spanLabel(span) {
  return sym("l") + " = " + fmt(span, 1) + " m";
}

/* Schraffur entlang p–q, Striche in Richtung d (Einspannung, Baugrund) */
function hatch(p, q, d, gap) {
  var n = Math.max(2, Math.round(Math.hypot(q.x - p.x, q.y - p.y) / gap)), out = "";
  for (var i = 0; i <= n; i++) {
    var a = vLerp(p, q, i / n);
    out += svgLine(a.x, a.y, a.x + d.x, a.y + d.y, "sysHatch");
  }
  return out;
}

function arrowHead(tip, from) {
  var u = vUnit(vSub(tip, from)), n = xy(-u.y, u.x), b = vAdd(tip, u, -7);
  return '<polyline class="sysArrow" points="' + polyPoints([vAdd(b, n, 4), tip, vAdd(b, n, -4)]) + '"/>';
}

function doubleArrow(p, q) {
  return svgLine(p.x, p.y, q.x, q.y, "sysArrow") + arrowHead(q, p) + arrowHead(p, q);
}

/* Auflager unter dem Balken (Spitze bei y): fest mit Schraffur, verschieblich mit Doppellinie */
function beamSupport(x, y, pinned) {
  var w = 11, yb = y + 16;
  var o = '<polygon class="sysSupport" points="' + polyPoints([xy(x, y), xy(x - w, yb), xy(x + w, yb)]) + '"/>';
  if (pinned) return o + svgLine(x - w - 4, yb, x + w + 4, yb, "sysGround") + hatch(xy(x - w - 2, yb), xy(x + w + 4, yb), xy(-5, 6), 6);
  return o + svgLine(x - w - 4, yb + 4, x + w + 4, yb + 4, "sysGround");
}

/* Einachsig: Balken (Beton wie im Querschnitt) mit zwei Auflagern bzw. beidseitiger Einspannung.
   Gleiche Breite wie der Querschnitt: 300, in der Mitte von 480. */
function beamSketch(fixed, span) {
  var x0 = 90, x1 = 390, y = 22, t = 10, o = [];
  /* Spannweite zwischen den Auflagerachsen bzw. Einspannungen */
  var xa = fixed ? x0 + 8 : x0 + 10, xb = fixed ? x1 - 8 : x1 - 10;
  if (fixed) {
    o.push('<rect class="secConcrete" x="' + xa + '" y="' + y + '" width="' + (xb - xa) + '" height="' + t + '"/>');
    [[x0, -1], [xb, 1]].forEach(function (w) {
      var xo = w[1] < 0 ? w[0] : w[0] + 8;
      o.push('<rect class="sysWall" x="' + w[0] + '" y="' + (y - 18) + '" width="8" height="' + (t + 36) + '"/>');
      o.push(hatch(xy(xo, y - 14), xy(xo, y + t + 18), xy(w[1] * 7, -5), 7));
    });
  } else {
    /* Balken etwas über die Auflager hinaus */
    o.push('<rect class="secConcrete" x="' + x0 + '" y="' + y + '" width="' + (x1 - x0) + '" height="' + t + '"/>');
    o.push(beamSupport(xa, y + t, true), beamSupport(xb, y + t, false));
  }
  o.push(dimAlong(xy(xa, 74), xy(xb, 74), spanLabel(span), 0, 17));
  return { body: o.join(""), h: 98 };
}

/* Zweiachsig: Betonplatte in Schrägansicht (Oberseite, Stirnseiten), Tragrichtungen als Doppelpfeile.
   Gleiche Grundfläche wie der Querschnitt: 300 breit, in der Mitte von 480. */
function slabSketch(support, span) {
  var A = xy(95, 86), B = xy(295, 86), d = xy(90, -70), C = vAdd(B, d), D = vAdd(A, d);
  var M = vLerp(A, C, 0.5), corners = [A, B, C, D], down = xy(0, 8), o = [];

  /* Stützen unter der Platte; die Platte ist dann leicht durchscheinend */
  if (support === "point") {
    [0.78, 0.22].forEach(function (v) {
      [0.22, 0.78].forEach(function (u) {
        var P = vAdd(vAdd(vLerp(A, B, u), d, v), down);
        o.push('<rect class="sysColumn" x="' + (P.x - 5) + '" y="' + P.y + '" width="10" height="30"/>');
      });
    });
  }
  var see = support === "point" ? " sysSeeThrough" : "";
  o.push('<polygon class="sysFaceFront' + see + '" points="' + polyPoints([A, B, vAdd(B, down), vAdd(A, down)]) + '"/>');
  o.push('<polygon class="sysFaceSide' + see + '" points="' + polyPoints([B, C, vAdd(C, down), vAdd(B, down)]) + '"/>');
  o.push('<polygon class="secConcrete' + see + '" points="' + polyPoints(corners) + '"/>');

  corners.forEach(function (p, i) {
    var q = corners[(i + 1) % 4], u = vUnit(vSub(q, p)), n = xy(u.y, -u.x);
    var mid = vLerp(p, q, 0.5);
    if ((mid.x - M.x) * n.x + (mid.y - M.y) * n.y < 0) n = xy(-n.x, -n.y);
    /* vorne und rechts unter der Stirnseite einspannen */
    if (support === "fixed") {
      var off = n.y > 0 ? down : xy(0, 0);
      o.push(hatch(vAdd(p, off), vAdd(q, off), vAdd(xy(n.x * 7, n.y * 7), u, -3), 8));
    }
    /* Platte läuft über die Stützen hinaus weiter: Bruchstriche an den Rändern */
    if (support === "point") o.push(svgLine(mid.x - n.x * 7 - u.x * 3, mid.y - n.y * 7 - u.y * 3, mid.x + n.x * 7 + u.x * 3, mid.y + n.y * 7 + u.y * 3, "sysGround"));
  });
  if (support === "simple") {
    /* Linienlager knapp innerhalb der Ränder */
    o.push('<polygon class="sysLineSupport" points="' + polyPoints(corners.map(function (p) { return vLerp(M, p, 0.93); })) + '"/>');
  }
  o.push(doubleArrow(vAdd(M, xy(-50, 0)), vAdd(M, xy(50, 0))));
  o.push(doubleArrow(vAdd(M, d, -0.32), vAdd(M, d, 0.32)));

  /* Masslinie vorne unter den Stützen hindurch */
  var dimY = support === "point" ? 42 : 24;
  o.push(dimAlong(vAdd(A, xy(0, dimY)), vAdd(B, xy(0, dimY)), spanLabel(span), 0, 17));
  o.push(dimAlong(vAdd(B, xy(24, 0)), vAdd(C, xy(24, 0)), spanLabel(span), 14, 4, "start"));
  return { body: o.join(""), h: 108 + dimY };
}

function systemLabel(s) {
  return s.name + (s.note ? " (" + s.note + ")" : "");
}

/* Skizze des statischen Systems unter dem Querschnitt */
function systemSketch(sysKey, span) {
  var s = T.systems[DB.systems[sysKey].sys];
  if (!s) return "";
  var g = s.axes === 1 ? beamSketch(s.support === "fixed", span) : slabSketch(s.support, span);
  return '<figure class="sysSketch"><svg class="secSvg" viewBox="0 0 480 ' + g.h + '" role="img" aria-label="Statisches System: ' +
    systemLabel(s) + '">' + g.body + '</svg><figcaption class="dbNote">Statisches System: ' + systemLabel(s) + "</figcaption></figure>";
}

/* ---------------------------------------------------------------------
   11. Diagramme
   --------------------------------------------------------------------- */
function axis(title, extra) {
  var a = {
    title: { text: title, font: { color: INK.secondary } },
    gridcolor: INK.grid, zeroline: false, linecolor: INK.grid, ticks: "outside", tickcolor: INK.grid
  };
  if (extra) Object.keys(extra).forEach(function (k) { a[k] = extra[k]; });
  return a;
}

function baseLayout(xTitle, yTitle, height) {
  return {
    margin: { l: 62, r: 16, t: 8, b: 46 },
    height: height,
    paper_bgcolor: INK.surface,
    plot_bgcolor: INK.surface,
    font: { family: "inherit", size: 12, color: INK.secondary },
    xaxis: axis(xTitle, { dtick: 1 }),
    yaxis: axis(yTitle, { rangemode: "tozero" }),
    hovermode: "closest",
    /* "normal": Plotly kehrt die Reihenfolge sonst wegen der Flächen (fill) um */
    legend: { orientation: "h", x: 0, y: -0.2, traceorder: "normal", itemwidth: 40, font: { color: INK.secondary } }
  };
}


/* Linie (Mittel) mit optionalem Band min–max über die Spannweite */
function spanTraces(points, name, color, meta, m, style) {
  style = style || 0;
  var xs = points.map(function (q) { return q.x; });
  var lo = points.map(function (q) { return q.lo; });
  var hi = points.map(function (q) { return q.hi; });
  var md = points.map(function (q) { return (q.lo + q.hi) / 2; });
  var d = ":." + m.dec + "f";
  var out = [];
  if (state.spread) {
    out.push({ x: xs, y: hi, type: "scatter", mode: "lines", line: { width: 0, color: color }, showlegend: false, hoverinfo: "skip" });
    out.push({ x: xs, y: lo, type: "scatter", mode: "lines", line: { width: 0, color: color }, fill: "tonexty",
      fillcolor: rgba(color, 0.16), showlegend: false, hoverinfo: "skip" });
  }
  out.push({
    x: xs, y: md, name: name, meta: meta, type: "scatter", mode: "lines+markers",
    customdata: points.map(function (q) { return [q.lo, q.hi]; }),
    line: { color: color, width: 2, dash: DASHES[style % DASHES.length].plotly },
    marker: { size: 8, color: color, symbol: SYMBOLS[style % SYMBOLS.length], line: { color: INK.surface, width: 2 } },
    hovertemplate: "<b>" + name + "</b><br>l = %{x} m<br>mittel %{y" + d + "} " + m.unit +
      "<br>min %{customdata[0]" + d + "} · max %{customdata[1]" + d + "}<extra></extra>"
  });
  return out;
}

function spanMarker(span, y) {
  return {
    x: [span], y: [y], type: "scatter", mode: "markers", showlegend: false, hoverinfo: "skip",
    marker: { size: 16, color: "rgba(0,0,0,0)", line: { color: INK.primary, width: 2 } }
  };
}

function spanLine(span) {
  return [{ type: "line", xref: "x", yref: "paper", x0: span, x1: span, y0: 0, y1: 1,
    line: { color: INK.muted, width: 1, dash: "dot" } }];
}

function bindClick(id, fn) {
  var node = el(id);
  if (node.dataset.bound) return;
  node.dataset.bound = "1";
  node.on("plotly_click", function (ev) {
    var pt = ev.points[0];
    if (pt && pt.data.meta) fn(pt.data.meta, pt.x);
  });
}

/* ---------------------------------------------------------------------
   12. Ebene 1 – Vergleich der Systeme
   --------------------------------------------------------------------- */
function renderLevel1() {
  var m = OVERVIEW.filter(function (x) { return x.key === state.metric; })[0];
  var active = activeSystems();
  var traces = [];

  active.forEach(function (s) {
    var pts = [];
    DB.spans[s.key].forEach(function (l) {
      var pr = pair(current({ sysKey: s.key, span: l }));
      if (pr.min && pr.max) pts.push({ x: l, lo: num(pr.min[m.col]), hi: num(pr.max[m.col]) });
    });
    var name = s.deck.name + ", " + DB.systems[s.key].name;
    traces = traces.concat(spanTraces(pts, name, s.deck.color, s.key, m, s.style));
  });

  var focusPair = state.focus ? pair(current()) : {};
  if (focusPair.min && focusPair.max) traces.push(spanMarker(state.span, mid(focusPair, m.col)));

  var layout = baseLayout("Spannweite l [m]", m.name + " [" + m.unit + "]", 380);
  layout.shapes = spanLine(state.span);
  layout.showlegend = active.length > 1;
  Plotly.react("plotOverview", traces, layout, pltConfig);
  bindClick("plotOverview", function (sysKey, x) {
    state.focus = sysKey;
    setSpan(x);
    render();
  });

  el("overviewEmpty").style.display = active.length ? "none" : "";
  renderPoint();
}

function renderPoint() {
  var box = el("pointBox");
  if (!state.focus || !activeSystems().length) { box.style.display = "none"; return; }
  box.style.display = "";

  var p = current();
  var deck = deckOf(p.sysKey);
  el("pointTitle").textContent = deck.name + ", " + DB.systems[p.sysKey].name + " · l = " + fmt(p.span, 1) + " m";

  var row = find(p, state.scen);
  var has = !!row;
  el("pointNoData").style.display = has ? "none" : "";
  el("pointNoData").textContent = T.noData + (DB.spans[p.sysKey].indexOf(p.span) === -1
    ? " Verfügbare Spannweiten: " + DB.spans[p.sysKey].map(function (l) { return fmt(l, 0); }).join(", ") + " m." : "");
  el("pointContent").style.display = has ? "" : "none";
  el("btnAddCompare").disabled = !has;
  el("btnDetail").disabled = !has;
  if (!has) return;

  console.log("Deckenrechner: CSV-Zeile " + row.line + " (" + T.scenNames[state.scen] + ")");
  el("pointSection").innerHTML = sectionSvg(row);
  el("pointSketch").innerHTML = systemSketch(p.sysKey, p.span);
  el("pointValues").innerHTML = valueTable(POINT_ROWS, null, [function (d) { return cell(row, d); }], isRibRow(row));

  var added = state.compare.some(function (q) { return sameVariant(q, p); });
  el("btnAddCompare").disabled = added;
  el("btnAddCompare").textContent = added ? "Im Vergleich enthalten" : "Zum Vergleich hinzufügen";
}

/* ---------------------------------------------------------------------
   13. Ebene 2 – Analyse der gewählten Varianten
   --------------------------------------------------------------------- */
function renderLevel2() {
  var list = state.compare;
  el("cmpEmpty").style.display = list.length ? "none" : "";
  el("cmpLegend").style.display = list.length ? "" : "none";

  var pairs = list.map(pair);
  el("cmpCount").textContent = list.length;
  if (state.cmpScen !== "all") {
    renderScenario(list, pairs, state.cmpScen);
    return;
  }
  /* Gemeinsame Skala, damit die Säulen der Karten vergleichbar sind */
  var yMax = 0;
  pairs.forEach(function (pr) {
    ["min", "max"].forEach(function (s) { if (pr[s]) yMax = Math.max(yMax, num(pr[s][COL.co2Tot])); });
  });

  el("cmpCards").innerHTML = list.map(function (p, i) {
    return '<div class="col-xl-6 mb-3"><div class="cBox p-3 h-100 cmpCard">' +
      '<div class="d-flex justify-content-between align-items-start">' +
      '<h5 class="panelTitle mb-1">Analyse ' + (i + 1) + " · " + deckOf(p.sysKey).name + "</h5>" +
      '<button type="button" class="close noPrint" data-action="remove" data-index="' + i + '" aria-label="Entfernen">&times;</button></div>' +
      '<div class="variantMeta">' + describe(p) + "</div>" +
      '<div id="cmpPlot' + i + '"></div><div id="cmpTable' + i + '"></div>' +
      '<button type="button" class="btn btn-sm btn-outline-primary mt-2 noPrint" data-action="detail" data-index="' + i + '">Detailanalyse</button>' +
      "</div></div>";
  }).join("");

  pairs.forEach(function (pr, i) {
    if (!pr.min || !pr.max) {
      el("cmpTable" + i).innerHTML = '<div class="alert alert-warning mb-0">' + T.noData + "</div>";
      return;
    }
    var x = [T.scenNames.min, T.scenNames.mean, T.scenNames.max];
    var traces = STACK.map(function (part) {
      return {
        type: "bar", x: x, name: part.name,
        y: [num(pr.min[part.col]), mid(pr, part.col), num(pr.max[part.col])],
        marker: { color: part.color, opacity: [1, 0.55, 1], line: { color: INK.surface, width: 2 } },
        hovertemplate: "<b>" + part.name + "</b><br>%{x}: %{y:.1f} kg CO₂eq/m²<extra></extra>"
      };
    });
    var totals = [num(pr.min[COL.co2Tot]), mid(pr, COL.co2Tot), num(pr.max[COL.co2Tot])];
    var layout = baseLayout("", "GWP [kg CO₂eq/m²]", 260);
    layout.barmode = "stack";
    layout.bargap = 0.45;
    layout.showlegend = false;
    /* Beschriftung der Säulen in der Kopfzeile der Tabelle direkt darunter */
    layout.margin = { l: ALIGN.label, r: ALIGN.unit, t: 22, b: 8 };
    layout.xaxis = axis("", { fixedrange: true, showticklabels: false });
    layout.yaxis = axis("GWP [kg CO₂eq/m²]", { range: [0, yMax * 1.12], fixedrange: true });
    layout.annotations = x.map(function (lbl, k) {
      return { x: lbl, y: totals[k], text: fmt(totals[k], 1), showarrow: false, yshift: 10,
        font: { color: INK.primary, size: 12 } };
    });
    Plotly.react("cmpPlot" + i, traces, layout, pltConfig);

    /* "GWP" links in der Kopfzeile, darüber nur min / mittel / max – sonst zu eng */
    el("cmpTable" + i).innerHTML = valueTable(CARD_ROWS, ["min", "mittel", "max"], [
      function (d) { return cell(pr.min, d); },
      function (d) { return d.mean ? fmt(mid(pr, d.col), d.dec) : "–"; },
      function (d) { return cell(pr.max, d); }
    ], isRibRow(pr.min), "GWP", true);
  });
}

/* Ein Szenario für alle Varianten: eine Säule je Variante, Tabelle mit den Varianten als Spalten */
function renderScenario(list, pairs, scen) {
  var names = list.map(function (p, i) { return "Analyse " + (i + 1); });
  var val = function (pr, col) {
    if (!pr.min || !pr.max) return NaN;
    return scen === "mean" ? mid(pr, col) : num(pr[scen][col]);
  };

  el("cmpCards").innerHTML = '<div class="col-12 mb-3"><div class="cBox p-3">' +
    '<h5 class="panelTitle mb-2">' + T.scenNames[scen] + " · alle Varianten</h5>" +
    '<div class="scenVariants">' + list.map(function (p, i) {
      return '<div class="scenVariant"><span><b>' + names[i] + "</b> · " + deckOf(p.sysKey).name + ", " + describe(p) +
        (pairs[i].min && pairs[i].max ? "" : ' <span class="text-warning">(' + T.noData + ")</span>") + "</span>" +
        '<span class="scenActions noPrint">' +
        '<button type="button" class="btn btn-link btn-sm p-0" data-action="detail" data-index="' + i + '">Detailanalyse</button>' +
        '<button type="button" class="close ml-3" data-action="remove" data-index="' + i + '" aria-label="Entfernen">&times;</button>' +
        "</span></div>";
    }).join("") + "</div>" +
    /* Diagramm und Tabelle im Druck nicht trennen (.cmpCard) */
    '<div class="cmpCard"><div id="cmpPlotAll"></div><div class="table-responsive" id="cmpTableAll"></div></div></div></div>';

  var traces = STACK.map(function (part) {
    return {
      type: "bar", x: names, name: part.name,
      y: pairs.map(function (pr) { return val(pr, part.col); }),
      customdata: list.map(describe),
      marker: { color: part.color, opacity: scen === "mean" ? 0.55 : 1, line: { color: INK.surface, width: 2 } },
      hovertemplate: "<b>%{x} · " + part.name + "</b><br>%{customdata}<br>%{y:.1f} kg CO₂eq/m²<extra></extra>"
    };
  });
  var totals = pairs.map(function (pr) { return val(pr, COL.co2Tot); });
  var yMax = Math.max.apply(null, totals.filter(isFinite).concat([0]));
  var layout = baseLayout("", T.scenNames[scen] + " [kg CO₂eq/m²]", 320);
  layout.barmode = "stack";
  layout.bargap = list.length < 3 ? 0.6 : 0.4;
  layout.showlegend = false;
  layout.margin = { l: ALIGN.label, r: ALIGN.unit, t: 22, b: 8 };
  layout.xaxis = axis("", { fixedrange: true, showticklabels: false });
  layout.yaxis = axis(T.scenNames[scen] + " [kg CO₂eq/m²]", { range: [0, yMax * 1.12], fixedrange: true });
  layout.annotations = names.map(function (n, k) {
    return { x: n, y: totals[k], text: fmt(totals[k], 1), showarrow: false, yshift: 10,
      font: { color: INK.primary, size: 12 } };
  });
  Plotly.react("cmpPlotAll", traces, layout, pltConfig);

  /* Zeilen für Rippen- und Flachdecken, falls beide vorkommen; leer, wo nicht zutreffend */
  var ribs = pairs.map(function (pr) { return isRibRow(pr.min); });
  var anyRib = ribs.some(Boolean), anyRec = ribs.some(function (r) { return !r; });
  el("cmpTableAll").innerHTML = valueTable(CARD_ROWS, names, pairs.map(function (pr, i) {
    return function (d) {
      if ((d.rib && !ribs[i]) || (d.rec && ribs[i])) return "";
      if (d.missing) return cell(null, d);
      if (!pr.min || !pr.max) return "–";
      if (scen === "mean") return d.mean ? fmt(mid(pr, d.col), d.dec) : "–";
      return cell(pr[scen], d);
    };
  }), anyRib && anyRec ? null : anyRib, T.scenNames[scen], true);
}

/* ---------------------------------------------------------------------
   14. Ebene 3 – Detailanalyse einer Variante
   --------------------------------------------------------------------- */
function renderLevel3() {
  var p = state.detail || current();
  var hasSys = p.sysKey && DB.systems[p.sysKey];
  el("detailBody").style.display = hasSys ? "" : "none";
  el("detailEmpty").style.display = hasSys ? "none" : "";
  if (!hasSys) return;

  el("detailTitle").textContent = "Detailanalyse · " + deckOf(p.sysKey).name;
  el("detailMeta").textContent = describe(p);
  el("btnDetailReset").style.display = state.detail ? "" : "none";

  /* Verlauf je Bemessungskriterium */
  var m = METRICS[state.detailMetric];
  var traces = [];
  DB.criteria.forEach(function (c) {
    var pts = [];
    DB.spans[p.sysKey].forEach(function (l) {
      var pr = pair(withParams(p, { crit: c, span: l }));
      if (pr.min && pr.max) pts.push({ x: l, lo: num(pr.min[m.col]), hi: num(pr.max[m.col]) });
    });
    traces = traces.concat(spanTraces(pts, T.critNames[c] || c, SERIES[critIndex(c) % SERIES.length], c, m));
  });
  var pr = pair(p);
  if (pr.min && pr.max) traces.push(spanMarker(p.span, mid(pr, m.col)));
  var layout = baseLayout("Spannweite l [m]", m.name + " [" + m.unit + "]", 340);
  layout.shapes = spanLine(p.span);
  layout.showlegend = true;
  layout.yaxis.rangemode = m.col === COL.medN ? "normal" : "tozero";
  Plotly.react("plotDetail", traces, layout, pltConfig);
  bindClick("plotDetail", function (crit, x) {
    if (state.detail) state.detail.span = x;
    else setSpan(x);
    render();
  });

  var has = pr.min && pr.max;
  el("detailNoData").style.display = has ? "none" : "";
  el("detailContent").style.display = has ? "" : "none";
  if (!has) return;

  var row = pr[state.scen];
  el("detailSection").innerHTML = sectionSvg(row);
  el("detailSketch").innerHTML = systemSketch(p.sysKey, p.span);
  el("detailTable").innerHTML = valueTable(DETAIL_ROWS, [T.scenNames.min, T.scenNames.max], [
    function (d) { return cell(pr.min, d); },
    function (d) { return cell(pr.max, d); }
  ], isRibRow(row));

  el("detailRawSummary").textContent = "Alle Spalten der Datenbank (CSV-Zeilen " + pr.min.line + " und " + pr.max.line + ")";
  el("detailRaw").innerHTML = '<table class="table table-sm rawTable"><thead><tr><th>Spalte</th>' +
    '<th class="text-right">' + T.scenNames.min + '</th><th class="text-right">' + T.scenNames.max + "</th></tr></thead><tbody>" +
    DB.head.map(function (h) {
      return "<tr><td><code>" + h + '</code></td><td class="text-right">' + pr.min[h] + '</td><td class="text-right">' + pr.max[h] + "</td></tr>";
    }).join("") + "</tbody></table>";

  el("detailFloor").innerHTML = "Nichttragender Bodenaufbau «" + (T.floorNames[p.floor] || p.floor) + "»: Höhe " + mm(num(row[COL.hFloor])) +
    " mm, Last " + fmt(row[COL.loadFloor], 2) + " kN/m², " + fmt(row[COL.co2Floor], 1) + " kg CO₂eq/m² über " +
    fmt(row[COL.lifespan], 0) + " Jahre Nutzungsdauer des Estrichs.";
}

/* ---------------------------------------------------------------------
   15. Speichern, Öffnen und Export

   Der Zustand wird im Browser gespeichert und lässt sich als Datei
   herunterladen und wieder öffnen. Beim Öffnen werden alle Werte gegen die
   aktuelle Datenbank geprüft. PDF über den Druckdialog, Excel mit SheetJS.
   --------------------------------------------------------------------- */
var STORE_KEY = "deckenrechner.v1";
var XLSX_URL = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
var CMP_SCENS = ["all", "min", "mean", "max"];
var LEVEL_NAMES = { 1: "Vergleich", 2: "Analyse", 3: "Detail" };

/* Parameter einer Variante mit Bezeichnung (Reihenfolge für Anzeige und Export) */
function paramDefs() {
  var keys = function (list) { return list.map(function (x) { return x.key; }); };
  return [
    { key: "sysKey", name: "Statisches System", options: Object.keys(DB.systems) },
    { key: "span", name: "Spannweite", options: DB.allSpans },
    { key: "cls", name: "Beton", options: keys(DB.classes) },
    { key: "refCls", name: "Betonstahl", options: keys(DB.rebarClasses) },
    { key: "floor", name: "Nichttragender Bodenaufbau", options: keys(DB.floors) },
    { key: "crit", name: "Bemessungskriterium", options: DB.criteria }
  ].concat(EXTRAS.map(function (e) { return { key: e.key, name: e.name, options: keys(DB.extras[e.key]) }; }));
}

function paramText(key, v) {
  var named = function (list) {
    var o = list.filter(function (x) { return x.key === v; })[0];
    return o ? o.name : clean(v);
  };
  if (key === "sysKey") return DB.systems[v] ? deckOf(v).name + ", " + DB.systems[v].name : clean(v);
  if (key === "span") return fmt(v, 1) + " m";
  if (key === "floor") return named(DB.floors);
  if (key === "crit") return T.critNames[v] || clean(v);
  if (key === "cls" || key === "refCls") return clean(v);
  var e = EXTRAS.filter(function (x) { return x.key === key; })[0];
  return e ? named(DB.extras[key]) : clean(v);
}

/* Parameter gegen die Datenbank prüfen; ungültige Werte durch die aktuelle Auswahl
   ersetzen und in issues melden. null, wenn das statische System fehlt. */
function sanitizeParams(p, label, issues) {
  if (!p || typeof p !== "object" || !DB.systems[p.sysKey]) {
    issues.push(label + ": statisches System «" + clean(p && p.sysKey) + "» nicht in der Datenbank, nicht übernommen");
    return null;
  }
  var fallback = current(), out = {};
  paramDefs().forEach(function (d) {
    if (d.options.indexOf(p[d.key]) !== -1) {
      out[d.key] = p[d.key];
    } else {
      out[d.key] = fallback[d.key];
      issues.push(label + ": " + d.name + " «" + clean(p[d.key]) + "» nicht in der Datenbank, ersetzt durch «" +
        paramText(d.key, out[d.key]) + "»");
    }
  });
  return out;
}

function gwpPair(p) {
  var pr = pair(p);
  return pr.min && pr.max ? { min: num(pr.min[COL.co2Tot]), max: num(pr.max[COL.co2Tot]) } : null;
}

function resetState() {
  state.systems = [];
  DB.decks.forEach(function (d) { if (d.available) state.systems.push(d.systems[0]); });
  state.focus = activeSystems()[0].key;
  state.floor = DB.floors[0].key;
  state.cls = DB.classes[0].key;
  state.refCls = DB.rebarClasses[0].key;
  state.crit = DB.criteria.indexOf("ENV") !== -1 ? "ENV" : DB.criteria[0];
  state.span = DB.spans[state.focus][0];
  EXTRAS.forEach(function (e) { state[e.key] = DB.extras[e.key][0].key; });
  state.spread = true;
  state.metric = "gwp";
  state.scen = "min";
  state.detailMetric = 0;
  state.cmpScen = "all";
  state.compare = [];
  state.detail = null;
}

/* Gesamter Zustand; GWP total der Varianten nur zum Vergleich beim Öffnen */
function snapshot() {
  return {
    app: "deckenrechner", version: 1, saved: new Date().toISOString(),
    inputs: current(), systems: state.systems.slice(), spread: state.spread, metric: state.metric,
    scen: state.scen, detailMetric: state.detailMetric, cmpScen: state.cmpScen, level: state.level,
    compare: state.compare.map(function (p) { return withParams(p, { gwp: gwpPair(p) }); }),
    detail: state.detail
  };
}

/* Übernimmt einen gespeicherten Zustand; false, wenn s kein Zustand des Deckenrechners ist */
function applySnapshot(s, issues) {
  if (!s || s.app !== "deckenrechner" || !s.inputs) return false;
  var systems = (Array.isArray(s.systems) ? s.systems : []).filter(function (k) {
    if (DB.systems[k]) return true;
    issues.push("Deckensysteme: «" + clean(k) + "» nicht in der Datenbank, nicht übernommen");
    return false;
  });
  if (systems.length) state.systems = systems;

  var p = sanitizeParams(s.inputs, "Eingabeparameter", issues);
  if (p) {
    paramDefs().forEach(function (d) { state[d.key === "sysKey" ? "focus" : d.key] = p[d.key]; });
  }
  if (state.systems.indexOf(state.focus) === -1) state.focus = activeSystems()[0].key;

  state.spread = s.spread !== false;
  state.metric = OVERVIEW.some(function (m) { return m.col && m.key === s.metric; }) ? s.metric : "gwp";
  state.scen = s.scen === "max" ? "max" : "min";
  state.detailMetric = METRICS[s.detailMetric] ? Number(s.detailMetric) : 0;
  state.cmpScen = CMP_SCENS.indexOf(s.cmpScen) !== -1 ? s.cmpScen : "all";
  state.level = [1, 2, 3].indexOf(s.level) !== -1 ? s.level : 1;

  state.compare = [];
  (Array.isArray(s.compare) ? s.compare : []).forEach(function (q, i) {
    var label = "Analyse " + (i + 1), v = sanitizeParams(q, label, issues);
    if (!v) return;
    var was = q.gwp, now = gwpPair(v);
    if (was && now && (Math.abs(was.min - now.min) > 0.05 || Math.abs(was.max - now.max) > 0.05)) {
      issues.push(label + ": GWP total beim Speichern " + fmt(was.min, 1) + "–" + fmt(was.max, 1) + ", jetzt " +
        fmt(now.min, 1) + "–" + fmt(now.max, 1) + " kg CO₂eq/m² (Datenbank geändert)");
    }
    state.compare.push(v);
  });
  state.detail = s.detail ? sanitizeParams(s.detail, "Detail", issues) : null;
  return true;
}

function save() {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(snapshot()));
  } catch (e) { /* Speicher nicht verfügbar – ohne Persistenz weiterarbeiten */ }
}

function load() {
  try {
    return applySnapshot(JSON.parse(localStorage.getItem(STORE_KEY) || "null"), []);
  } catch (e) {
    return false;
  }
}

/* Bedienelemente auf den Zustand setzen (nach Öffnen oder Zurücksetzen) */
function syncControls() {
  fillSelect("selClass", DB.classes, state.cls);
  fillSelect("selRebarClass", DB.rebarClasses, state.refCls);
  fillSelect("selFloor", DB.floors, state.floor);
  EXTRAS.forEach(function (e) { fillSelect(e.sel, DB.extras[e.key], state[e.key]); });
  syncDeckList();
  setSpan(state.span);
  el("chkSpread").checked = state.spread;
  setButtons("metric", state.metric);
  setButtons("scen", state.scen);
  setButtons("cmpscen", state.cmpScen);
  el("selDetailMetric").value = String(state.detailMetric);
  var tab = el("tab" + state.level);
  if (!tab.classList.contains("active")) $(tab).tab("show");
}

function today() {
  var d = new Date();
  return d.getFullYear() + "-" + ("0" + (d.getMonth() + 1)).slice(-2) + "-" + ("0" + d.getDate()).slice(-2);
}

function download(blob, name) {
  var a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 0);
}

function saveFile() {
  var blob = new Blob([JSON.stringify(snapshot(), null, 1)], { type: "application/json" });
  download(blob, "Deckenrechner_" + today() + ".json");
}

function esc(s) {
  return String(s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; });
}

function openFile(file) {
  var reader = new FileReader();
  reader.onload = function () {
    var s;
    try { s = JSON.parse(reader.result); } catch (e) { s = null; }
    if (!s || s.app !== "deckenrechner") {
      window.alert("«" + file.name + "» ist keine gespeicherte Datei des Deckenrechners.");
      return;
    }
    if (!window.confirm("Die aktuelle Auswahl und die Varianten der Analyse durch den Inhalt von «" + file.name + "» ersetzen?")) return;
    var issues = [];
    applySnapshot(s, issues);
    showOpenNotice(file.name, issues);
    syncControls();
    render();
  };
  reader.readAsText(file);
}

/* Hinweis nach dem Öffnen: was nicht übernommen werden konnte oder sich geändert hat */
function showOpenNotice(fileName, issues) {
  var node = el("openNotice");
  node.className = "alert alert-dismissible openNotice " + (issues.length ? "alert-warning" : "alert-success");
  node.innerHTML = "<div>«" + esc(fileName) + "» geöffnet." +
    (issues.length ? " Bitte prüfen:" : " Alle Eingaben wurden unverändert übernommen.") + "</div>" +
    (issues.length ? "<ul>" + issues.map(function (t) { return "<li>" + esc(t) + "</li>"; }).join("") + "</ul>" : "") +
    '<button type="button" class="close" aria-label="Schliessen">&times;</button>';
  node.hidden = false;
}

/* Varianten für Excel: die der Analyse, sonst der gewählte Punkt */
function exportVariants() {
  if (state.compare.length) {
    return state.compare.map(function (p, i) { return { name: "Analyse " + (i + 1), p: p }; });
  }
  return [{ name: "Gewählter Punkt", p: current() }];
}

/* Zahl für Excel; Text bleibt Text, fehlende Werte bleiben leer */
function xlValue(v) {
  if (typeof v === "number") return isFinite(v) ? v : "";
  if (v === undefined || v === null || String(v).trim() === "") return "";
  return isNaN(Number(v)) ? clean(v) : Number(v);
}

function plain(html) {
  return String(html).replace(/<[^>]+>/g, "");
}

function exportExcel() {
  var variants = exportVariants();

  var inputs = [["Eingabeparameter", "Wert"], ["Export", new Date().toLocaleString(LOCALE)]];
  inputs.push(["Deckensysteme (Vergleich)", activeSystems().map(function (s) { return paramText("sysKey", s.key); }).join("; ")]);
  paramDefs().forEach(function (d) {
    inputs.push([d.key === "sysKey" ? "Gewählter Punkt" : d.name, paramText(d.key, d.key === "sysKey" ? state.focus : state[d.key])]);
  });

  var metrics = OVERVIEW.filter(function (m) { return m.col; });
  var overview = [["Deckensystem", "Statisches System", "Spannweite [m]"].concat([].concat.apply([], metrics.map(function (m) {
    return ["min", "mittel", "max"].map(function (s) { return m.label + " " + s + " [" + m.unit + "]"; });
  })))];
  activeSystems().forEach(function (s) {
    DB.spans[s.key].forEach(function (l) {
      var pr = pair(current({ sysKey: s.key, span: l }));
      if (!pr.min || !pr.max) return;
      overview.push([s.deck.name, DB.systems[s.key].name, l].concat([].concat.apply([], metrics.map(function (m) {
        return [xlValue(pr.min[m.col]), xlValue(mid(pr, m.col)), xlValue(pr.max[m.col])];
      }))));
    });
  });

  var pairs = variants.map(function (v) { return pair(v.p); });
  var analysis = [["Kennwert", "Einheit"].concat([].concat.apply([], variants.map(function (v) {
    return [v.name + " " + T.scenNames.min, v.name + " " + T.scenNames.mean, v.name + " " + T.scenNames.max];
  })))];
  analysis.push(["Variante", ""].concat([].concat.apply([], variants.map(function (v) {
    return [deckOf(v.p.sysKey).name + ", " + describe(v.p), "", ""];
  }))));
  DETAIL_ROWS.forEach(function (g) {
    analysis.push([g.title]);
    g.rows.forEach(function (d) {
      if (d.missing) return;
      analysis.push([plain(d.name), d.unit || ""].concat([].concat.apply([], pairs.map(function (pr) {
        var rib = isRibRow(pr.min);
        if (!pr.min || !pr.max || (d.rib && !rib) || (d.rec && rib)) return ["", "", ""];
        return [xlValue(pr.min[d.col]), d.mean ? xlValue(mid(pr, d.col)) : "", xlValue(pr.max[d.col])];
      }))));
    });
  });

  var raw = [["Spalte"].concat([].concat.apply([], variants.map(function (v) {
    return [v.name + " " + T.scenNames.min, v.name + " " + T.scenNames.max];
  })))];
  raw.push(["CSV-Zeile"].concat([].concat.apply([], pairs.map(function (pr) {
    return [pr.min ? pr.min.line : "", pr.max ? pr.max.line : ""];
  }))));
  DB.head.forEach(function (h) {
    raw.push([h].concat([].concat.apply([], pairs.map(function (pr) {
      return [pr.min ? xlValue(pr.min[h]) : "", pr.max ? xlValue(pr.max[h]) : ""];
    }))));
  });

  var wb = XLSX.utils.book_new();
  [["Eingaben", inputs, 30], ["Vergleich", overview, 22], ["Analyse", analysis, 36], ["Datenbank", raw, 32]].forEach(function (sh) {
    var ws = XLSX.utils.aoa_to_sheet(sh[1]);
    ws["!cols"] = sh[1][0].map(function (_, i) { return { wch: i === 0 ? sh[2] : 18 }; });
    XLSX.utils.book_append_sheet(wb, ws, sh[0]);
  });
  XLSX.writeFile(wb, "Deckenrechner_" + today() + ".xlsx");
}

/* SheetJS erst bei Bedarf laden */
function withXlsx(fn) {
  if (window.XLSX) return fn();
  var sc = document.createElement("script");
  sc.src = XLSX_URL;
  sc.onload = fn;
  sc.onerror = function () { window.alert("Die Excel-Bibliothek konnte nicht geladen werden."); };
  document.head.appendChild(sc);
}

/* PDF über den Druckdialog des Browsers («Als PDF sichern»): gedruckt wird die
   aktive Ebene mit den Eingabeparametern im Kopf; Diagramme in Blattbreite
   (A4 abzüglich Rand 2 × 12 mm), in Bootstrap-Spalten abzüglich deren Abstand,
   damit die Säulen der Analyse über ihren Tabellenspalten bleiben */
var PRINT_WIDTH = 703;
var PRINT_GUTTER = 30;

function printPlots() {
  return document.querySelectorAll(".tab-pane.active .js-plotly-plot");
}

function preparePrint() {
  el("printTitle").textContent = "Deckenrechner · " + LEVEL_NAMES[state.level];
  el("printDate").textContent = new Date().toLocaleDateString(LOCALE);
  var rows = [["Deckensysteme", activeSystems().map(function (s) { return paramText("sysKey", s.key); }).join("; ")]];
  paramDefs().forEach(function (d) {
    if (d.key === "sysKey" || d.key === "span") return;
    rows.push([d.name, paramText(d.key, state[d.key])]);
  });
  el("printInputs").innerHTML = rows.map(function (r) { return "<tr><th>" + esc(r[0]) + "</th><td>" + esc(r[1]) + "</td></tr>"; }).join("");
  Array.prototype.forEach.call(printPlots(), function (node) {
    Plotly.relayout(node, { width: PRINT_WIDTH - (node.closest(".row") ? PRINT_GUTTER : 0) });
  });
}

/* afterprint kann noch im Drucklayout eintreffen; erst danach neu zeichnen */
function restoreAfterPrint() {
  setTimeout(function () {
    if (window.matchMedia("print").matches) return restoreAfterPrint();
    render();
    Array.prototype.forEach.call(printPlots(), function (node) { Plotly.Plots.resize(node); });
  }, 100);
}

function bindFileActions() {
  el("btnSave").addEventListener("click", saveFile);
  el("btnOpen").addEventListener("click", function () { el("fileOpen").click(); });
  el("fileOpen").addEventListener("change", function () {
    if (this.files[0]) openFile(this.files[0]);
    this.value = "";
  });
  el("openNotice").addEventListener("click", function (ev) {
    if (ev.target.closest(".close")) this.hidden = true;
  });
  el("btnReset").addEventListener("click", function () {
    if (!window.confirm("Alle Eingaben auf die Standardwerte setzen und die Varianten der Analyse entfernen?")) return;
    resetState();
    el("openNotice").hidden = true;
    syncControls();
    render();
  });
  el("btnPdf").addEventListener("click", function () { window.print(); });
  el("btnExcel").addEventListener("click", function () { withXlsx(exportExcel); });
  window.addEventListener("beforeprint", preparePrint);
  window.addEventListener("afterprint", restoreAfterPrint);
}

/* ---------------------------------------------------------------------
   16. Aktualisierung
   --------------------------------------------------------------------- */
function render() {
  el("cmpCount").textContent = state.compare.length;
  if (state.level === 1) renderLevel1();
  else if (state.level === 2) renderLevel2();
  else renderLevel3();
  save();
}

/* ---------------------------------------------------------------------
   17. Start
   --------------------------------------------------------------------- */
function init(csv) {
  buildIndex(csv);
  resetState();
  /* Zuletzt im Browser gespeicherter Zustand, sonst Standardwerte */
  if (!load()) resetState();

  buildControls();
  bindFileActions();
  syncControls();

  el("loadingNote").style.display = "none";
  el("app").style.visibility = "visible";
  render();
}

document.addEventListener("DOMContentLoaded", function () {
  var url = document.body.getAttribute("data-csv") || "data/deckenrechner.csv";
  fetch(url)
    .then(function (r) {
      if (!r.ok) throw new Error(r.status + " " + r.statusText);
      return r.text();
    })
    .then(function (text) { init(parseCSV(text)); })
    .catch(function (err) {
      console.error(err);
      el("loadingNote").className = "alert alert-danger";
      el("loadingNote").textContent = T.loadError + " (" + err.message + ")";
    });
});
